import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { ApolloError } from '@apollo/client/errors'
import {
  Cascader,
  Checkbox,
  Error,
  Modal,
  Text,
  TimeRangePicker,
} from 'components/common'
import { CustomizedModalProps } from './types'
import { time } from 'lib/str-formatter/datetime'

export interface TimezoneSelectorModalCascader {
  key: number | string
  title: string
  variants: {
    start: string
    end: string
    id: string
    variantId?: string
    category: string
  }[]
}

export interface TimezoneSelectorModalProps
  extends Omit<CustomizedModalProps, 'onOk'> {
  dataSource: TimezoneSelectorModalCascader[]
  onOk: (value: [moment.Moment, moment.Moment], category?: string) => void
  error?: ApolloError
  rateId: MutableRefObject<string>
  checkManualPeriodTime?: boolean
  setManualPeriodTime?: (value: boolean) => void
  setRateVariantId?: (value: string) => void
}

export interface TimezoneCascaderOptionType {
  id?: string
  category?: string
  start?: string
  end?: string
  variantId?: string
  children?: Array<TimezoneCascaderOptionType>
}

export const TimezoneSelectorModal = ({
  checkManualPeriodTime,
  dataSource,
  error,
  onOk,
  rateId,
  setManualPeriodTime,
  setRateVariantId,
  ...rest
}: TimezoneSelectorModalProps): JSX.Element => {
  const date = moment().format('YYYY-MM-DD')
  const [cascader, setCascader] = useState<string>(null)
  const [timezone, setTimezone] = useState<[moment.Moment, moment.Moment]>(null)
  const [disabled, setDisabled] = useState<boolean>(true)
  const rateTitle = useRef<string>(null)

  const options = useMemo<Array<TimezoneCascaderOptionType>>(() => {
    return dataSource?.map((preset) => ({
      value: preset.title,
      label: preset.title,
      children: preset.variants.map((variant) => ({
        value: `${time(variant.start)} - ${time(variant.end)}`,
        label: `${time(variant.start)} - ${time(variant.end)}`,
        id: variant.id,
        variantId: variant.variantId,
        category: preset.title,
        start: variant.start,
        end: variant.end,
      })),
    }))
  }, [dataSource])

  const displayRender = useCallback(() => {
    return cascader
  }, [cascader])

  const onCascaderChange = (
    value: string[],
    selectedOptions?: TimezoneCascaderOptionType[]
  ) => {
    if (value?.length > 0 && selectedOptions?.length > 0) {
      const { id, start, end, category, variantId } =
        selectedOptions[selectedOptions.length - 1]
      setCascader(value[value.length - 1])
      handleTimezone([start, end], category, id)
      setRateVariantId(variantId)
    } else {
      setCascader(null)
      handleTimezone(null)
      setRateVariantId(null)
    }
  }

  const handleTimezone = (
    value?: string[],
    category?: string,
    rate?: string
  ) => {
    rateTitle.current = category || null
    rateId.current = rate || null

    if (!value) {
      setTimezone(null)
      return
    }
    setTimezone([moment(`${date} ${value[0]}`), moment(`${date} ${value[1]}`)])
  }

  const handleCheck = (checked: boolean) => {
    setManualPeriodTime(checked)
    setDisabled(!checked)
    if (checked) {
      setCascader(null)
      handleTimezone(null)
      setRateVariantId(null)
    }
  }

  const handleSubmit = () => {
    onOk(timezone, rateTitle.current)
  }

  if (error) return <Error message={error.message} />

  return (
    <Modal
      title="予約する時間帯を選択"
      okText="決定"
      cancelText="キャンセル"
      onOk={handleSubmit}
      {...rest}
    >
      <Cascader
        options={options}
        expandTrigger="click"
        displayRender={displayRender}
        onChange={onCascaderChange}
        disabled={!disabled}
      />
      <div className="mt-4 mb-2">
        <Checkbox
          checked={checkManualPeriodTime}
          onChange={() => handleCheck(!checkManualPeriodTime)}
        >
          開始時刻と終了時刻を手動設定します
        </Checkbox>
      </div>
      <div>
        <TimeRangePicker
          value={timezone}
          disabled={disabled}
          onChange={(_, timeStrings) => handleTimezone(timeStrings)}
        />
      </div>
    </Modal>
  )
}
